/**
 * ViewPortUI - 视口尺寸显示组件
 * 显示 .main-container 的尺寸信息，支持窗口 resize 事件实时更新
 */

import { BootstrapUI, bootstrapUI, ElementOptions } from '../bootstrapui';
import { MsgViewUI, MsgViewOptions } from '../ui/msgViewUI';

export interface ViewPortOptions extends ElementOptions {
    /** 标签文本 */
    label?: string;
    /** 容器样式类 */
    containerClass?: string;
    /** 标签样式类 */
    labelClass?: string;
    /** 内容容器样式类 */
    contentContainerClass?: string;
    /** 标签是否与内容同行显示 */
    isLabelInline?: boolean;
    /** 是否自动监听窗口 resize 事件 */
    autoResize?: boolean;
    /** 尺寸更新间隔（毫秒） */
    updateInterval?: number;
}

export class ViewPortUI {
    private ui: BootstrapUI;
    private container: HTMLElement;
    private options: ViewPortOptions;
    private msgViewUI: MsgViewUI;
    private onChangeCallbacks: ((size: { width: number; height: number }) => void)[] = [];
    private contentContainerElement: HTMLElement;
    private resizeHandler: (() => void) | null = null;
    private updateTimer: number | null = null;
    private mainContainer: HTMLElement | null = null;

    constructor(options: ViewPortOptions = {}) {
        this.ui = bootstrapUI;
        this.options = {
            label: '视口尺寸',
            containerClass: 'panel-setting-item',
            labelClass: 'panel-setting-item-label',
            contentContainerClass: 'panel-content-container',
            isLabelInline: true,
            autoResize: true,
            readOnly: true,
            updateInterval: 100,
            i18nKey: 'label.viewportSize',
            ...options
        };

        this.container = this.createElement();
        this.msgViewUI = this.createMsgViewUI();
        this.setupEventHandlers();
        this.initializeMainContainer();
    }

    /**
     * 创建视口显示组件元素
     * @returns 包含标签和内容区域的容器元素
     */
    private createElement(): HTMLElement {
        const container = this.ui.createElement({
            tag: 'div',
            className: 'panel-setting-flex-item'
        });

        // 创建标签
        const labelElement = this.ui.createElement({
            tag: 'div',
            textContent: this.options.label || '',
            className: 'panel-setting-sub-title',
            i18nKey: this.options.i18nKey,
        });
        container.appendChild(labelElement);

        // 创建内容容器
        const contentContainer = this.ui.createElement({
            tag: 'div',
            className: ['w100', 'd-flex', 'flex-gap-75'],
        });
        this.contentContainerElement = contentContainer;
        container.appendChild(contentContainer);

        return container;
    }

    /**
     * 创建 MsgViewUI 组件
     */
    private createMsgViewUI(): MsgViewUI {
        const msgViewOptions: MsgViewOptions = {
            label: '',
            showLabel: false,
            info: '获取中...',
            type: 'info',
            containerClass: ['panel-setting-item', 'viewport-msg-container'],
            isLabelInline: this.options.isLabelInline,
            displayDirection: 'horizontal',
            scrollable: false,
            placeholder: '暂无尺寸信息'
        };

        const msgView = new MsgViewUI(msgViewOptions);
        this.contentContainerElement.appendChild(msgView.getContainer());
        return msgView;
    }

    /**
     * 初始化主容器引用
     */
    private initializeMainContainer(): void {
        this.mainContainer = document.querySelector('.main-container') as HTMLElement;
        if (!this.mainContainer) {
            console.warn('ViewPortUI: 未找到 .main-container 元素');
            this.msgViewUI.setInfo('未找到主容器');
            return;
        }

        // 初始更新
        this.update();
    }

    /**
     * 设置事件处理器
     */
    private setupEventHandlers(): void {
        if (this.options.autoResize) {
            this.startResizeListener();
        }

        // 为 MsgViewUI 添加变化监听
        this.msgViewUI.onChange((info) => {
            this.notifyChange();
        });
    }

    /**
     * 开始监听窗口 resize 事件
     */
    private startResizeListener(): void {
        this.resizeHandler = () => {
            if (this.updateTimer) {
                clearTimeout(this.updateTimer);
            }

            this.updateTimer = window.setTimeout(() => {
                this.update();
            }, this.options.updateInterval);
        };

        window.addEventListener('resize', this.resizeHandler);
    }

    /**
     * 停止监听窗口 resize 事件
     */
    private stopResizeListener(): void {
        if (this.resizeHandler) {
            window.removeEventListener('resize', this.resizeHandler);
            this.resizeHandler = null;
        }

        if (this.updateTimer) {
            clearTimeout(this.updateTimer);
            this.updateTimer = null;
        }
    }

    /**
     * 更新尺寸信息
     */
    update(): void {
        if (!this.mainContainer) {
            this.msgViewUI.setInfo('主容器未找到');
            return;
        }

        try {
            const rect = this.mainContainer.getBoundingClientRect();
            const width = Math.round(rect.width);
            const height = Math.round(rect.height);

            this.msgViewUI.setInfo([
                `w:${width}`,
                `h:${height}`
            ])
            this.notifyChange();
        } catch (error) {
            console.error('ViewPortUI: 更新尺寸时出错', error);
            this.msgViewUI.setInfo('更新失败');
        }
    }

    /**
     * 通知尺寸变化
     */
    private notifyChange(): void {
        const size = this.getSize();
        this.onChangeCallbacks.forEach(callback => {
            try {
                callback(size);
            } catch (error) {
                console.error('ViewPortUI变化回调执行错误:', error);
            }
        });
    }

    /**
     * 获取当前尺寸
     */
    getSize(): { width: number; height: number } {
        if (!this.mainContainer) {
            return { width: 0, height: 0 };
        }

        try {
            const rect = this.mainContainer.getBoundingClientRect();
            return {
                width: Math.round(rect.width),
                height: Math.round(rect.height)
            };
        } catch (error) {
            console.error('ViewPortUI: 获取尺寸时出错', error);
            return { width: 0, height: 0 };
        }
    }

    /**
     * 获取宽度
     */
    getWidth(): number {
        return this.getSize().width;
    }

    /**
     * 获取高度
     */
    getHeight(): number {
        return this.getSize().height;
    }

    /**
     * 设置自动监听状态
     */
    setAutoResize(autoResize: boolean): void {
        this.options.autoResize = autoResize;

        if (autoResize) {
            this.startResizeListener();
        } else {
            this.stopResizeListener();
        }
    }

    /**
     * 是否自动监听
     */
    isAutoResize(): boolean {
        return this.options.autoResize || false;
    }

    /**
     * 设置更新间隔
     */
    setUpdateInterval(interval: number): void {
        this.options.updateInterval = interval;
    }

    /**
     * 获取更新间隔
     */
    getUpdateInterval(): number {
        return this.options.updateInterval || 100;
    }

    /**
     * 是否禁用
     */
    isDisabled(): boolean {
        return this.msgViewUI.isDisabled();
    }

    /**
     * 设置禁用状态
     */
    setDisabled(disabled: boolean): void {
        this.msgViewUI.setDisabled(disabled);
    }

    /**
     * 设置组件激活状态（启用/禁用整个组件）
     */
    setActive(status: boolean): void {
        this.msgViewUI.setActive(status);
    }

    /**
     * 是否只读
     */
    isReadOnly(): boolean {
        return this.msgViewUI.isReadOnly();
    }

    /**
     * 设置只读状态
     */
    setReadOnly(readonly: boolean): void {
        this.msgViewUI.setReadOnly(readonly);
    }

    /**
     * 添加尺寸变化监听器
     */
    onChange(callback: (size: { width: number; height: number }) => void): void {
        this.onChangeCallbacks.push(callback);
    }

    /**
     * 移除尺寸变化监听器
     */
    offChange(callback: (size: { width: number; height: number }) => void): void {
        const index = this.onChangeCallbacks.indexOf(callback);
        if (index > -1) {
            this.onChangeCallbacks.splice(index, 1);
        }
    }

    /**
     * 获取容器元素
     */
    getContainer(): HTMLElement {
        return this.container;
    }

    /**
     * 获取 MsgViewUI 组件
     */
    getMsgViewUI(): MsgViewUI {
        return this.msgViewUI;
    }

    /**
     * 获取标签文本
     */
    getLabel(): string {
        return this.options.label || '';
    }

    /**
     * 设置标签文本
     */
    setLabel(label: string): void {
        this.options.label = label;
        const labelElement = this.container.querySelector('.panel-setting-sub-title');
        if (labelElement) {
            labelElement.textContent = label;
        }
    }

    /**
     * 手动触发更新（用于测试或特殊情况）
     */
    forceUpdate(): void {
        this.update();
    }

    /**
     * 销毁组件
     */
    destroy(): void {
        this.onChangeCallbacks = [];

        // 停止监听
        this.stopResizeListener();

        // 销毁 MsgViewUI 组件
        this.msgViewUI.destroy();

        // 移除DOM元素
        if (this.container.parentNode) {
            this.container.parentNode.removeChild(this.container);
        }
    }
}

// 导出默认实例创建函数
export function createViewPortUI(options: ViewPortOptions = {}): ViewPortUI {
    return new ViewPortUI(options);
}
